/**
 * InvoiceFormLineItems
 *
 * Line items table with add/remove/edit per-item controls.
 */

import { useEffect, useState } from 'react'

import { LineItemRow, LineItemsTableHeader, isVendorInvoiceType, useUomOptions } from './InvoiceFormLineItems-parts'
import { tryLoadAccountsFromApi } from '../../../pages/journal-entry-form/useJournalEntryFormEffects-helpers'
import { catalogItemDisplayName, type CatalogItem } from '../../../services/catalog-service'
import { useOrganization } from '../../../stores/organization-store'
import { AccountSearchModal } from '../../shared/AccountSearchModal'
import { ItemPickerModal } from '../../shared/ItemPickerModal'
import {
  Button,
  Table,
  TableBody,
} from '../../ui'

import type { UseInvoiceFormReturn } from './useInvoiceForm'
import type { Account } from '../../../services/account-service'
import type { SelectOption } from '../../ui'

interface InvoiceFormLineItemsProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
}

interface ItemPickerState {
  pickerForId: string | null
  setPickerForId: (v: string | null) => void
  pendingQtyFocus: string | null
  setPendingQtyFocus: (v: string | null) => void
}

function useItemPickerState(): ItemPickerState {
  const [pickerForId, setPickerForId] = useState<string | null>(null)
  const [pendingQtyFocus, setPendingQtyFocus] = useState<string | null>(null)
  useEffect(() => {
    if (!pendingQtyFocus) { return }
    const raf = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-qty-row=""]`)
      el?.focus()
      setPendingQtyFocus(null)
    })
    return () => cancelAnimationFrame(raf)
  }, [pendingQtyFocus])
  return { pickerForId, setPickerForId, pendingQtyFocus, setPendingQtyFocus }
}

// Accounts for the purchase-line override picker. Only fetched for vendor
// documents — sales invoices never need this list, so skip the request.
function useLineItemAccounts(invoiceType: string): Account[] {
  const { organizationId } = useOrganization()
  const [accounts, setAccounts] = useState<Account[]>([])
  useEffect(() => {
    if (!organizationId || !isVendorInvoiceType(invoiceType)) { return }
    let isActive = true
    void tryLoadAccountsFromApi(organizationId).then((result) => {
      if (isActive && result) { setAccounts(result) }
    })
    return () => { isActive = false }
  }, [organizationId, invoiceType])
  return accounts
}

interface AccountPickerState {
  accountPickerForId: string | null
  setAccountPickerForId: (v: string | null) => void
}

function useAccountPickerState(): AccountPickerState {
  const [accountPickerForId, setAccountPickerForId] = useState<string | null>(null)
  return { accountPickerForId, setAccountPickerForId }
}

interface ItemPickerHandlerInput {
  pickerForId: string | null
  setPickerForId: (v: string | null) => void
  setPendingQtyFocus: (v: string | null) => void
  updateLineItem: UseInvoiceFormReturn['updateLineItem']
}

function buildItemPickerHandler(input: ItemPickerHandlerInput): (catalogItem: CatalogItem) => void {
  const { pickerForId, setPickerForId, setPendingQtyFocus, updateLineItem } = input
  return (catalogItem) => {
    if (!pickerForId) { return }
    // Capture the catalog link so posting can record an inventory movement.
    updateLineItem(pickerForId, 'itemId', catalogItem.id)
    updateLineItem(pickerForId, 'itemCode', catalogItem.code ?? '')
    updateLineItem(pickerForId, 'description', catalogItemDisplayName(catalogItem))
    if (catalogItem.hsnCode) {
      updateLineItem(pickerForId, 'hsnCode', catalogItem.hsnCode)
      updateLineItem(pickerForId, 'sacCode', '')
    } else if (catalogItem.sacCode) {
      updateLineItem(pickerForId, 'hsnCode', '')
      updateLineItem(pickerForId, 'sacCode', catalogItem.sacCode)
    }
    const unit = catalogItem.baseUom?.symbol ?? catalogItem.baseUom?.name ?? ''
    if (unit) { updateLineItem(pickerForId, 'unit', unit) }
    setPendingQtyFocus(pickerForId)
    setPickerForId(null)
  }
}

function buildAccountPickerHandler(
  accountPickerForId: string | null,
  setAccountPickerForId: (v: string | null) => void,
  updateLineItem: UseInvoiceFormReturn['updateLineItem'],
): (account: Account) => void {
  return (account) => {
    if (!accountPickerForId) { return }
    updateLineItem(accountPickerForId, 'accountId', account.id)
    setAccountPickerForId(null)
  }
}

interface LineItemsTableBodyProps {
  form: UseInvoiceFormReturn
  isLoading: boolean
  uomOptions: SelectOption[]
  accounts: Account[]
  onPickItem: (id: string) => void
  onPickAccount: (id: string) => void
}

function LineItemsTableBody({ form, isLoading, uomOptions, accounts, onPickItem, onPickAccount }: LineItemsTableBodyProps): JSX.Element {
  const { lineItems, invoiceType, currency, calculateLineItem, removeLineItem, updateLineItem } = form
  return (
    <TableBody>
      {lineItems.map((item, index) => (
        <LineItemRow
          key={item.id}
          accounts={accounts}
          calculated={calculateLineItem(item)}
          currency={currency}
          index={index}
          invoiceType={invoiceType}
          isLoading={isLoading}
          isRemoveDisabled={isLoading || lineItems.length <= 1}
          item={item}
          uomOptions={uomOptions}
          onPickAccount={() => onPickAccount(item.id)}
          onPickItem={() => onPickItem(item.id)}
          onRemove={() => removeLineItem(item.id)}
          onUpdate={(field, value) => updateLineItem(item.id, field, value)}
        />
      ))}
    </TableBody>
  )
}

export function InvoiceFormLineItems({ form, isLoading }: InvoiceFormLineItemsProps): JSX.Element {
  const { invoiceType, addLineItem, updateLineItem } = form
  const uomOptions = useUomOptions()
  const picker = useItemPickerState()
  const accountPicker = useAccountPickerState()
  const accounts = useLineItemAccounts(invoiceType)

  return (
    <div className="card overflow-hidden">
      <h3 className="text-xs font-bold tracking-wider uppercase px-3 py-2 bg-primary-solid text-primary-foreground rounded-t">
        Line Items
      </h3>
      <Table className="compact-table">
        <LineItemsTableHeader invoiceType={invoiceType} />
        <LineItemsTableBody
          accounts={accounts} form={form} isLoading={isLoading} uomOptions={uomOptions}
          onPickAccount={(id) => accountPicker.setAccountPickerForId(id)}
          onPickItem={(id) => picker.setPickerForId(id)}
        />
      </Table>
      <div className="el-p-md el-pt-xs">
        <Button disabled={isLoading} type="button" variant="secondary" onClick={addLineItem}>
          + Add Line Item
        </Button>
      </div>
      <AccountSearchModal
        accounts={accounts}
        isOpen={accountPicker.accountPickerForId !== null}
        onClose={() => accountPicker.setAccountPickerForId(null)}
        onSelect={buildAccountPickerHandler(accountPicker.accountPickerForId, accountPicker.setAccountPickerForId, updateLineItem)}
      />
      <ItemPickerModal
        isOpen={picker.pickerForId !== null}
        onClose={() => picker.setPickerForId(null)}
        onSelect={buildItemPickerHandler({
          pickerForId: picker.pickerForId,
          setPickerForId: picker.setPickerForId,
          setPendingQtyFocus: picker.setPendingQtyFocus,
          updateLineItem,
        })}
      />
    </div>
  )
}
